import { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router"
import { faUserPlus } from "@fortawesome/pro-regular-svg-icons"

import { ApiError } from "../../../../../core/api/errorTypes"
import { fetchUserWallets } from "../../settings/wallets/refill/refillApi"
import { SelectWalletDialog } from "../../settings/wallets/refill/components/SelectWalletDialog"
import { MakePurchaseDialog } from "../../settings/wallets/refill/components/MakePurchaseDialog"
import { UserWalletsRefillState } from "../../settings/wallets/refill/pages/UserWalletsRefill"
import { UserWallet } from "../../settings/wallets/refill/refillTypes"
import { useOrderDialogUrlState } from "../../../../../shared/components/createOrder/hooks/useOrderDialogUrlState"
import { getUserDetails, UserQuery } from "../../../../users/userApi"
import { CreateOrderType } from "../../../../../shared/components/createOrder/helpers"
import { TakeawayOrderHost } from "../../../../../shared/components/createOrder/components/takeaway/TakeawayOrderHost"
import { selectUserId } from "../../../../login/loginSelectors"
import { useAppSelector } from "../../../../store/storeHooks"
import {
    TopBarNewButtonMenu,
    TopBarNewMenuSection,
} from "../../../../../shared/components/topBarNewButtonMenu/TopBarNewButtonMenu"
import { OrderOnBehalfFlow } from "../../../../../shared/components/createOrder/orderOnBehalf/OrderOnBehalfFlow"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { getRefillWalletRoute } from "../../../../../constants/routes"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { moduleItemFactories, UserNewMenuSectionId } from "./moduleItemFactories"
import { useMatchEnvironment } from "../../../../environment/envHooks"
import { EnvironmentType } from "../../../../environment/envTypes"
import { ModuleName } from "../../../../users/userTypes"
import { UserType } from "../../../../login/loginTypes"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"

export function UserCreateNewButton() {
    const userId = useAppSelector(selectUserId)
    const { openDialog } = useOrderDialogUrlState()
    const navigate = useNavigate()
    const isAppPaymentEnabled = useMatchEnvironment([EnvironmentType.Local, EnvironmentType.Test])
    const isRefillWalletEnabled = !useMatchEnvironment([EnvironmentType.QA, EnvironmentType.Prod])

    const [openSelectWalletDialog, setOpenSelectWalletDialog] = useState(false)
    const [openMakePurchaseDialog, setOpenMakePurchaseDialog] = useState(false)
    const [openTakeawayDialog, setOpenTakeawayDialog] = useState(false)
    const [openOnBehalfDialog, setOpenOnBehalfDialog] = useState(false)
    const [userWallets, setUserWallets] = useState<Array<UserWallet> | null>(null)
    const [walletErrorMessage, setWalletErrorMessage] = useState<string | null>(null)

    const { mutateAsync } = useApiMutation(fetchUserWallets, {
        async onSuccess(_, data) {
            setUserWallets(data.wallets)
            setWalletErrorMessage(null)
        },
        async onError(_, error) {
            const message = error instanceof ApiError ? error.displayMessage : null
            setWalletErrorMessage(message)
            setOpenSelectWalletDialog(!!message)
        },
    })

    const { data: userDetails } = useApiQuery(getUserDetails, {
        queryName: UserQuery.USER_DETAILS,
        dependencies: {
            userId: userId ?? 0,
        },
    })

    const handleCreateOrder = useCallback(
        (type: CreateOrderType) => {
            openDialog({
                activeOrderTab: type,
            })
        },
        [openDialog],
    )

    const handleRefillWallet = useCallback(async () => {
        const response = await mutateAsync({ userId: userId ?? 0 })

        if (response.wallets?.length === 1) {
            const state: UserWalletsRefillState = {
                wallet: response.wallets[0],
            }
            navigate(getRefillWalletRoute(response.wallets[0].acquirer.acquirerSetupId, response.wallets[0]?.id), {
                state,
            })
        } else {
            setOpenSelectWalletDialog(true)
        }
    }, [mutateAsync, userId, navigate])

    const handleOpenMakePurchase = useCallback(() => {
        setOpenMakePurchaseDialog(true)
    }, [])

    const handleOpenTakeaway = useCallback(() => {
        setOpenTakeawayDialog(true)
    }, [])

    const handleCloseTakeaway = useCallback(() => {
        setOpenTakeawayDialog(false)
    }, [])

    const handleCloseSelectWallet = useCallback(() => {
        setOpenSelectWalletDialog(false)
        setWalletErrorMessage(null)
    }, [])

    const handleSelectWallet = useCallback(
        (wallet: UserWallet) => {
            setOpenSelectWalletDialog(false)
            const state: UserWalletsRefillState = { wallet }
            navigate(getRefillWalletRoute(wallet.acquirer.acquirerSetupId, wallet.id), { state })
        },
        [navigate],
    )

    const handleCloseMakePurchase = useCallback(() => {
        setOpenMakePurchaseDialog(false)
    }, [])

    const handleOpenOnBehalf = useCallback(() => {
        setOpenOnBehalfDialog(true)
    }, [])

    const handleCloseOnBehalf = useCallback(() => {
        setOpenOnBehalfDialog(false)
    }, [])

    const handleOnBehalfComplete = useCallback(
        (colleagueId: number, colleagueName: string) => {
            setOpenOnBehalfDialog(false)
            openDialog({ onBehalfOf: { userId: colleagueId, displayName: colleagueName } })
        },
        [openDialog],
    )

    const activeModules = useMemo(
        () => new Set(userDetails?.user?.modules?.filter((m) => m.isActive)?.map((m) => m.name)),
        [userDetails],
    )

    const sections = useMemo(() => {
        const factories = moduleItemFactories({
            handleCreateOrder,
            handleOpenTakeaway,
            handleOpenMakePurchase,
            handleRefillWallet,
        })
            .filter((factory) => activeModules.has(factory.moduleName))
            .filter((factory) => factory.moduleName !== ModuleName.APP_PAYMENT || isAppPaymentEnabled)
            .filter((factory) => factory.moduleName !== ModuleName.REFILL_WALLET || isRefillWalletEnabled)

        const itemsFor = (sectionId: UserNewMenuSectionId) =>
            factories.filter((factory) => factory.section === sectionId).map((factory) => factory.create())

        const allSections: ReadonlyArray<TopBarNewMenuSection> = [
            {
                id: "booking",
                title: (
                    <LocalizedStrict id="top-bar-new-menu-section-booking-title">
                        <>Booking</>
                    </LocalizedStrict>
                ),
                items: itemsFor("booking"),
                action: {
                    icon: faUserPlus,
                    label: (
                        <LocalizedStrict id="top-bar-new-menu-book-for-another-user-button">
                            <>Book for another user</>
                        </LocalizedStrict>
                    ),
                    onClick: handleOpenOnBehalf,
                },
            },
            {
                id: "purchase",
                title: (
                    <LocalizedStrict id="top-bar-new-menu-section-purchase-title">
                        <>Purchase</>
                    </LocalizedStrict>
                ),
                items: itemsFor("purchase"),
            },
        ]

        return allSections.filter((section) => section.items.length > 0)
    }, [
        handleCreateOrder,
        handleOpenTakeaway,
        handleOpenMakePurchase,
        handleRefillWallet,
        handleOpenOnBehalf,
        activeModules,
        isAppPaymentEnabled,
        isRefillWalletEnabled,
    ])

    return (
        <>
            <TopBarNewButtonMenu sections={sections} />

            {openOnBehalfDialog && (
                <OrderOnBehalfFlow
                    userType={UserType.USER}
                    onComplete={handleOnBehalfComplete}
                    handleCloseParentDialog={handleCloseOnBehalf}
                />
            )}

            {openTakeawayDialog && <TakeawayOrderHost open={openTakeawayDialog} onClose={handleCloseTakeaway} />}

            {openSelectWalletDialog && (
                <SelectWalletDialog
                    open={openSelectWalletDialog}
                    onClose={handleCloseSelectWallet}
                    onSelect={handleSelectWallet}
                    userWallets={userWallets}
                    errorMessage={walletErrorMessage}
                />
            )}

            {openMakePurchaseDialog && (
                <MakePurchaseDialog open={openMakePurchaseDialog} onClose={handleCloseMakePurchase} />
            )}
        </>
    )
}
